// app logo — the Abstergo/Animus mark supplied by the client, recolourable per-user. The active
// colourway comes from Settings (prefs.logo); pass `variant` to force a specific one (the Settings
// previews do this). 'stock' is the original silver. A theme that draws its own mark (lib/themes, mark:
// 'ink', e.g. Stegi) gets the flat wave in var(--mark-ink) on the top bar instead (pass `themed`).

import type { CSSProperties } from 'react';
import { useSettings } from '../settings/SettingsContext.js';
import { logoDef, type LogoVariant } from '../lib/logos.js';

/** The ROLODEX wave mark as inline SVG, filled with currentColor — for letterheads / printable
 *  documents where it must take the document ink and print reliably (an <img> can't be recoloured,
 *  and the 3D canvas logo can't print). Same artwork as the in-app logo. */
export function RolodexMark({ className, title = 'ROLODEX', style }: { className?: string; title?: string; style?: CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="2 10 24 8" role="img" aria-label={title} fill="currentColor">
      <path d="M3.65 11.79C4.27 11.96 4.89 12.22 5.52 12.28C6.14 12.34 6.76 12.24 7.39 12.17C8.01 12.1 8.64 11.96 9.26 11.84C9.88 11.72 10.51 11.55 11.13 11.46C11.76 11.36 12.38 11.22 13 11.26C13.63 11.3 14.25 11.57 14.87 11.72C15.5 11.86 16.12 11.97 16.75 12.15C17.37 12.34 17.99 12.71 18.62 12.84C19.24 12.96 19.87 12.96 20.49 12.93C21.11 12.89 21.74 12.73 22.36 12.63L22.36 12.73C21.74 12.97 21.11 13.29 20.49 13.43C19.87 13.58 19.24 13.67 18.62 13.62C17.99 13.57 17.37 13.3 16.75 13.14C16.12 12.97 15.5 12.78 14.87 12.65C14.25 12.53 13.63 12.39 13 12.4C12.38 12.41 11.76 12.6 11.13 12.71C10.51 12.82 9.88 12.94 9.26 13.07C8.64 13.2 8.01 13.4 7.39 13.49C6.76 13.58 6.14 13.7 5.52 13.61C4.89 13.53 4.27 13.2 3.65 12.99Z" />
      <path d="M5.65 14.95C6.43 15.15 7.21 15.53 7.99 15.57C8.77 15.61 9.55 15.31 10.33 15.18C11.11 15.04 11.89 14.89 12.67 14.78C13.45 14.67 14.23 14.49 15.01 14.52C15.79 14.54 16.57 14.74 17.35 14.94C18.13 15.13 18.91 15.48 19.69 15.71C20.47 15.93 21.25 16.27 22.03 16.29C22.81 16.31 23.59 15.97 24.37 15.8L24.37 15.93C23.59 16.28 22.81 16.87 22.03 16.98C21.25 17.09 20.47 16.79 19.69 16.62C18.91 16.44 18.13 16.08 17.35 15.92C16.57 15.76 15.79 15.66 15.01 15.67C14.23 15.68 13.45 15.83 12.67 15.98C11.89 16.12 11.11 16.38 10.33 16.53C9.55 16.68 8.77 16.96 7.99 16.89C7.21 16.82 6.43 16.38 5.65 16.12Z" />
    </svg>
  );
}

export function LogoMark({
  size = 28,
  className,
  glow,
  variant,
  themed,
}: {
  size?: number;
  className?: string;
  glow?: boolean;
  /** force a specific colourway (defaults to the user's preference). */
  variant?: LogoVariant;
  /** let the active theme draw the mark (the top bar): an ink theme gets the flat currentColor wave. */
  themed?: boolean;
}) {
  const { prefs, uiTheme } = useSettings();
  const ink = uiTheme.mark === 'ink';
  if (themed && ink && !variant) {
    // same artwork, as wide as the colourway image draws inside its square box
    return <RolodexMark className={`logo-ink${className ? ` ${className}` : ''}`} title="Rolodex" style={{ width: size }} />;
  }
  const def = logoDef(variant ?? prefs.logo);
  return (
    <img
      src={def.src}
      alt="Rolodex"
      width={size}
      height={size}
      className={className}
      style={{
        objectFit: 'contain',
        display: 'block',
        // an ink theme is flat: no glow on the sign-in screens either
        filter: glow && !ink ? `drop-shadow(0 0 12px ${def.glow})` : undefined,
      }}
    />
  );
}
